import * as go from 'gojs/release/go-module';

import { SELF_LINK_CURVINESS } from 'src/containers/ProjectDiagram/constants/styles';
import { LinkSelfLoopProperties } from 'src/containers/ProjectDiagram/types/link';

export class ExtendedLink extends go.Link {
  private _skipsLayoutCommit = false;

  /**
   * Gets or sets whether this part should be positioned during a layout.
   * The initial and normal value is false.
   *
   * This differs from #isLayoutPositioned in that setting this value to `true`
   * allows this part to still be considered in determining the layout of other
   * parts, but this part itself will not be not repositioned.
   */
  get skipsLayoutCommit() {
    return this._skipsLayoutCommit;
  }

  set skipsLayoutCommit(value: boolean) {
    this._skipsLayoutCommit = value;
  }

  private _isNewlyAdded = true;

  /**
   * Gets or sets whether this link has just been created, and not yet had a
   * layout applied to it. The initial value is true, and should remain false
   * after the first PARTIAL or FULL layout has been applied.
   *
   * This ensures that go.js initially applies a shape that is consistent with
   * non-circular bounding boxes, as well as consistent with subsequent
   * interactions
   */
  get isNewlyAdded() {
    return this._isNewlyAdded;
  }

  set isNewlyAdded(value: boolean) {
    this._isNewlyAdded = value;
  }

  cloneProtected(copy: this) {
    super.cloneProtected(copy);
    // eslint-disable-next-line no-param-reassign
    copy.skipsLayoutCommit = this.skipsLayoutCommit;
    // eslint-disable-next-line no-param-reassign
    copy.isNewlyAdded = this.isNewlyAdded;
  }

  computeThickness() {
    if (this.data.labelKeys.length) {
      return super.computeThickness();
    }
    return 100;
  }

  computeSelfLoopProperties(): LinkSelfLoopProperties | null {
    if (
      !this.data ||
      !this.fromNode?.key ||
      !this.toNode?.key ||
      this.fromNode.key !== this.toNode.key
    ) {
      return null;
    }

    // Compute a stable (arbitrary) index number based on the order of self link keys
    const allSiblingSelfLinks: string[] = [];
    this.fromNode.linksConnected.each((link) => {
      if (
        link.fromNode &&
        link.toNode &&
        link.fromNode.key === link.toNode.key
      ) {
        allSiblingSelfLinks.push(link.key as string);
      }
    });
    const loopIndex = allSiblingSelfLinks.sort().indexOf(this.key as string);
    // Stagger arcs in fixed positions on two sides of the node,
    // increasing the radius when there are more than two self-loops
    const alternateSide = loopIndex % 2 === 1;
    const stackDepth = Math.floor(loopIndex / 2);

    return {
      alternateSide,
      stackDepth,
    };
  }

  computeCurviness() {
    const selfLoopProperties = this.computeSelfLoopProperties();
    if (!selfLoopProperties) {
      return super.computeCurviness();
    }
    return SELF_LINK_CURVINESS * (1 + selfLoopProperties.stackDepth);
  }

  computeSpot(from: boolean, port?: go.GraphObject) {
    const selfLoopProperties = this.computeSelfLoopProperties();
    if (!selfLoopProperties) {
      return super.computeSpot(from, port);
    }

    if (selfLoopProperties.alternateSide) {
      if (from) {
        return go.Spot.Bottom;
      }
      return go.Spot.Left;
    }
    if (from) {
      return go.Spot.Top;
    }
    return go.Spot.Right;
  }
}
